import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from "styled-components";
import moment from 'moment';
import { withStyles, ThemeProvider } from '@mui/styles';
import { createTheme } from '@mui/material/styles';
import { StyledEngineProvider } from '@mui/material';
import Input from '@mui/material/Input';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { TableSortLabel as ReactTableSortLabel } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import { FilterList as FilterIcon } from '@mui/icons-material';
import Resizable from './Resizable';
import { setGridSetting } from './utils/storage';
import {
  density,
  getDensityClass,
  getCheckboxWidth,
  getBadgeColumnWidth,
  getMinColumnWidth,
  getFilterIconStyle
} from './utils/density';
import { WithTracking } from "../WithTracking";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Box from "@mui/material/Box";
const TableSortLabel = WithTracking(ReactTableSortLabel);

// Change the height of a ""<TableHead>/<TableRow>"
// see https://github.com/mui-org/material-ui/issues/6492#issuecomment-425188779
const GRID_HEAD_THEME = createTheme({
  overrides:{
    MuiTableRow: {
      root: { height: 20 },
      head: { height: 20 }
    },
    MuiTableCell: {
      root: {
        fontSize: '0.75rem'
      },
      head: {
        lineHeight: 'normal',
        color: 'rgba(0,0,0,0.57)'
      }
    }
  }
});

const styles = (/* theme */) => ({
  root: {
    position: 'relative'
  },
  filterCell: {
    margin: '0 0 3px 0',
    padding: '0 2px 0 2px',
    color: 'rgba(0,0,0,0.57)',
    fontSize: '13px',
    fontWeight: 'normal'
  },
  ...density
});

const noop = () => {};

const MIN_WIDTH_PX = 80;

const FilterPrompt = styled.div`
  cursor: pointer;
  color: #aaaaaa;
  font-style: italic;
  opacity: 0;
  &: hover{
    opacity: 1;
  }
`;

const FilterValue = styled.div`
  cursor: pointer;
  color: #444444;
  font-weight: bold;
`;

class GridHead extends Component {

  constructor(props) {
    super(props);

    this.cellRefs = {};
    this.canDrag = true;
    this.draggableId = -1;
    this.draggableParent = null;

    this.filterFieldRefs = {};
    const { columns } = props;
    columns.forEach((c) => {
      this.filterFieldRefs[c.id] = React.createRef();
    });

    this.state = {
      filterCriteria: props.filterCriteria,
      columns: props.columns,
      isDragging: false,
      numSelected: props.numSelected,
      editFilterId: 0
    };
  }

  static getDerivedStateFromProps(props, prevState) {
    const { columns, numSelected } = props;
    if (columns !== prevState.columns || numSelected !== prevState.numSelected) {
      return { columns, numSelected };
    } else {
      return null;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { columns, numSelected } = this.props;
    if (prevProps.numSelected !== numSelected) {
      this.setState({ numSelected });
    }
    if (prevProps.columns !== columns) {
      const { filterCriteria } = this.state;
      columns.forEach((c) => {
        const ref = this.filterFieldRefs[c.id];
        if (ref && ref.current && ref.current.inputRef) {
          const filter = filterCriteria.find(c.id);
          if (filter) {
            ref.current.inputRef.value = filter.displayValue;
          }
        }
      });
      this.setState({ columns });
    }
  }

  createSortHandler = property => event => {
    this.hover(false)(event);
    this.props.onRequestSort(event, property);
  };

  /** **************************
   *     COLUMN REORDERING
   ************************** */

  setBorder = (elementId, visible) => {
    this.draggableParent.childNodes.forEach(node => {
      node.style.borderRight = 'none';
    });
    if (this.canDrag && visible) {
      const element = this.draggableParent.childNodes[elementId];
      if (element) {
        element.style.borderRight = '2px solid #000';
      }
    }
  };

  // @TODO - limit dragging vertically
  dragStart = evt => {
    if (!this.canDrag) return;
    evt.stopPropagation();
    evt.dataTransfer.effectAllowed = 'move';

    const element = evt.target;
    if (element){
      this.draggableId = element.getAttribute('id');
      this.draggableParent = element.parentElement;
      this.setState({ isDragging: true })
    }
  }

  dragEnter = evt => {
    if (!this.canDrag) return;
    evt.dataTransfer.dropEffect = 'move';

    let element = evt.target;
    if (element) {
      if (element.nodeName !== 'TH') {
        while ((element = element.parentElement) && element && element.nodeName !== 'TH'); // eslint-disable-line no-cond-assign
      }

      if (element && element.nodeName === 'TH' && this.setBorder) {
        this.setBorder(element.getAttribute('id'), true);
      }
    }
  }

  dragOver = evt => {
    if (!this.canDrag) return;
    evt.preventDefault();
  }

  drop = evt => {
    if (!this.canDrag) return;
    evt.stopPropagation();
    evt.preventDefault();

    let element = evt.target;
    if (element) {
      if (element.nodeName !== 'TH') {
        while ((element = element.parentElement) && element && element.nodeName !== 'TH'); // eslint-disable-line no-cond-assign
      }

      const columns = this.state.columns.slice(0);
      if (element && element.nodeName === 'TH') {
        const fromIndex = this.draggableId;
        const toIndex = element.getAttribute('id');
        this.setBorder(toIndex, false);
        if (fromIndex !== toIndex) {
          const srcCol = columns[fromIndex];
          columns.splice(fromIndex, 1);
          columns.splice(toIndex, 0, srcCol);

          this.setState({ columns, isDragging: false });
          this.props.onColumnReorder(columns);
          this.saveOrder(columns);
        }
      }
    }
  }

  saveOrder = columns => {
    const { title, settings } = this.props;
    if (settings.autosaveOrder) {
      setGridSetting(title, 'columns', columns);
    }
  }

  /** **************************
   *     COLUMN RESIZING
   ************************** */

  hover = hovered => evt => {
    if (!this.canDrag) return;

    const { columns } = this.state;
    let element = evt.target;
    if (element) {
      if (element.nodeName !== 'TH') {
        while ((element = element.parentElement) && element && element.nodeName !== 'TH'); // eslint-disable-line no-cond-assign
      }
      if (element){
        const targetId = element.getAttribute('id');
        columns[targetId].isHovered = hovered;

        this.setState({columns });
      }
    }
  };

  resizeStarted = () => {
    this.canDrag = false;
  }

  resizeEnded = () => {
    const { title, settings } = this.props;
    this.canDrag = true;
    const columns = this.state.columns.map(col => Object.assign(col, { isHovered: false }));
    if (!settings || (settings && !!settings.autosaveWidth)) {
      setGridSetting(title, 'columns', columns);
    }
    this.forceUpdate();
  }

  resizeColumn = (idx, width) => {
    const { columns } = this.state;
    columns[idx].width = width > MIN_WIDTH_PX ? width : MIN_WIDTH_PX;

    this.setState({ columns });
    this.props.onColumnResize(columns);
  }

  /** *******************
   *     RENDERING
   ******************* */

  createRef = id => {
    const ref = React.createRef();
    this.cellRefs[id] = ref;
    return ref;
  }

  // This makes the sort direction arrow pointing to the same direction as in DFP UI
  // material-ui renders 'asc' as an arrow pointing up
  flipSortDirection = direction => direction === 'asc' ? 'desc' : 'asc';

  getSortLabel = (col, field, direction) => {
    if (direction === 'asc') return 'Sort Descending';

    if (field !== col.id && direction === 'desc') {
      return 'Sort Descending';
    }

    return 'Sort Ascending';
  }

  setFilterInput = (columnId, isSet) => {
    const {editFilterId} = this.state;
    this.setState({editFilterId: editFilterId === columnId ? 0 : columnId})
  }

  createFilterCell = (column, idx, columns) => {
      const { classes, settings } = this.props;
      if (!column.visible) return null;
      const { filterCriteria, editFilterId } = this.state;

      const { width } = column;
      let inputField = null;

      if (column.filterable) {
        const filter = filterCriteria.find(column.id);
        const filterFieldRef = this.filterFieldRefs[column.id];
        if (filter.displayValue) {
          inputField = <FilterValue
            onClick={e => this.setFilterInput(column.id, true)}>
            {filter.displayValue}
          </FilterValue>
        } else if (!editFilterId) {
          inputField = <FilterPrompt
            onClick={e => this.setFilterInput(column.id, true)}>
            {"click to filter"}
          </FilterPrompt>;
        }
        if (filter.isDate && editFilterId === column.id) {
          inputField = (
            <LocalizationProvider key={filter.id} dateAdapter={AdapterMoment}>
              <DatePicker
                clearable
                id={filter.id}
                value={filter.value ? moment(filter.value) : null}
                inputFormat={filter.dateFmt}
                onChange={date => (filter.value = date)}
                onClose={e => this.setFilterInput(column.id, false)}
                className={classes.filterCell}
                style={{ width: width - 26 }}
                InputProps={{
                  style: {
                    fontWeight: 'normal',
                    color: 'rgba(0,0,0,0.57)',
                    fontSize: '13px'
                  }
                }}
                animateYearScrolling={false}
                renderInput={({ inputRef, inputProps, InputProps }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center'}}>
                    <input ref={inputRef} {...inputProps} />
                    {InputProps?.endAdornment}
                  </Box>
                )}
              />
            </LocalizationProvider>
          );
        } else if (editFilterId === column.id) {
          setTimeout(() => {
            const inputElement = filterFieldRef.current;
            if (inputElement) {
              inputElement.focus()
            }
          }, 100)
          inputField = (
            <Input
              inputRef={filterFieldRef}
              type="text"
              className={classes.filterCell}
              style={{ width: Math.max(0, (width || 0) - 26) }}
              defaultValue={filter.displayValue}
              onChange={this.deferFilterChange(filter, 300)}
              onBlur={e => this.setFilterInput(column.id, false)}
            />
          );
        }
      }

      return (
        <TableCell
          style={{
            width: width || "",
            paddingTop: 0,
            paddingBottom: 0
          }}
          key={column.id}
          classes={{
            root: getDensityClass(classes, 'Cell', settings.density)
          }}
        >
          { inputField }
        </TableCell>
      );
  }

  createCell = (column, idx, columns, extraStyles) => {
    const { sortBy, sort, sortable, plain, classes, settings, showFilterRow } = this.props;
    if (column.visible) {
      let { width } = column;

      if (!width) {
        width = column.badge ? getBadgeColumnWidth(settings.density) : getMinColumnWidth(settings.density);
      }

      const style = Object.assign({
        width: width || ""
      }, extraStyles || {})

      if (showFilterRow) {
        style.borderBottom = 'none';
        style.paddingBottom = 0;
      }

      return (
        <TableCell
          id={idx}
          classes={{
            root: getDensityClass(classes, 'Cell', settings.density),
            paddingCheckbox: getDensityClass(classes, 'CheckboxCell', settings.density)
          }}
          className={classes.root}
          style={{ overflow: 'hidden', ...style }}
          key={column.id}
          sortDirection={sortBy === column.id ? sort : false}
          draggable={!plain && this.canDrag}
          onDragStart={!plain ? this.dragStart : noop}
          onDragEnter={!plain ? this.dragEnter : noop}
          onDragOver={!plain ? this.dragOver : noop}
          onDrop={!plain ? this.drop : noop}
          onMouseEnter={!plain ? this.hover(true) : noop}
          onMouseLeave={!plain ? this.hover(false) : noop}
        >
          {!plain && (
          <Resizable
            cellRef={this.createRef(idx)}
            idx={idx}
            settings={settings}
            originalWidth={column.originalWidth}
            isHovered={column.isHovered && !this.state.isDragging}
            onStart={this.resizeStarted}
            onEnd={this.resizeEnded}
            onResize={this.resizeColumn}
          />
          )}
          {sortable ? (
            <Tooltip
              title={this.getSortLabel(column, sortBy, sort)}
              placement={column.numeric ? 'top-end' : 'top-start'}
              enterDelay={300}
            >
              <TableSortLabel
                active={sortBy === column.id}
                direction={this.flipSortDirection(sort)}
                onClick={this.createSortHandler(column.id)}
                tracking={{action:"Sort Column", label:column.id}}
              >
                {column.label}
              </TableSortLabel>
            </Tooltip>
          ) : (
            column.label
          )
          }
        </TableCell>
      );
    }
    return null;
  }

  createFilterRow = () => {
    const { columns } = this.state;

    if (!columns.some(c => c.filterable))
      return undefined;

    const {
      showFilterRow,
      selectable,
      classes,
      settings
    } = this.props;

    if (!showFilterRow) {
      return null;
    }

    const cellStyle = {
      ...getCheckboxWidth(settings.density)
    };

    return (
      <TableRow>
        { (selectable ? (
          <TableCell
            padding="checkbox"
            style={cellStyle}
            classes={{
              root: getDensityClass(classes, 'Cell', settings.density),
              paddingCheckbox: getDensityClass(classes, 'CheckboxCell', settings.density)
            }}
          >
            <div style={getFilterIconStyle(settings.density)}>
              <FilterIcon style={{ height: '20px', width: '20px' }}  />
            </div>
          </TableCell>
        ) : null) }
        { (columns.map(this.createFilterCell)) }
      </TableRow>
    );
  }

  deferFilterChange = (filter, timeout) => (evt) => {
    const { value } = evt.target;
    const key = filter.id;
    this.deferredChanges = this.deferredChanges || {};
    if (this.deferredChanges[key]) {
      clearTimeout(this.deferredChanges[key].timer);
    }
    this.deferredChanges[key] = {
      timer: setTimeout(() => { filter.value = value; }, timeout)
    };
  };

  render() {
    const { columns, numSelected } = this.state;
    const {
      onSelectAllClick,
      rowCount,
      selectable,
      classes,
      settings,
      showFilterRow
    } = this.props;

    const cellStyle = {
      ...getCheckboxWidth(settings.density)
    };
    if (showFilterRow) {
      cellStyle.borderBottom = 'none';
      cellStyle.paddingBottom = 0;
    }

    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={GRID_HEAD_THEME}>
          <TableHead>
            <TableRow>
              {(selectable) ? (
                <TableCell
                  padding="checkbox"
                  style={cellStyle}
                  classes={{
                    root: getDensityClass(classes, 'Cell', settings.density),
                    paddingCheckbox: getDensityClass(classes, 'CheckboxCell', settings.density)
                  }}
                >
                  <Checkbox
                    color="primary"
                    classes={{
                      root: getDensityClass(classes, 'Checkbox', settings.density)
                    }}
                    indeterminate={numSelected > 0 && numSelected < rowCount}
                    checked={numSelected > 0 && numSelected === rowCount}
                    onChange={onSelectAllClick}
                    type="checkbox"
                  />
                </TableCell>
              ): null
              }
              {columns.map(this.createCell)}
            </TableRow>
            {this.createFilterRow()}
          </TableHead>
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }
}

GridHead.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  sort: PropTypes.string.isRequired,
  sortBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
  showFilterRow: PropTypes.bool.isRequired
};

export default withStyles(styles)(GridHead);
